import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, FocusEventHandler, KeyboardEvent, KeyboardEventHandler, SelectHTMLAttributes } from 'react';
import { matchOption, moveOption, popupPosition, readOptions } from './select-options.ts';
import '../../styles/select.css';

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>,
  'multiple' | 'size' | 'value' | 'defaultValue' | 'onKeyDown' | 'onFocus' | 'onBlur'> & {
  value?: string | number;
  defaultValue?: string | number;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
};

export function Select({
  children, value, defaultValue, onChange, disabled, required, id, name, className, style,
  autoFocus, title, onKeyDown, onFocus, onBlur, onInvalid, ...nativeProps
}: SelectProps) {
  const generatedId = useId();
  const triggerId = id ?? `select-${generatedId}`;
  const listId = `${triggerId}-list`;
  const options = useMemo(() => readOptions(children), [children]);
  const initial = useRef(String(defaultValue ?? options.find(option => !option.disabled)?.value ?? ''));
  const [localValue, setLocalValue] = useState(initial.current);
  const selectedValue = String(value ?? localValue);
  const selectedIndex = options.findIndex(option => option.value === selectedValue);
  const selected = options[selectedIndex];
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [invalid, setInvalid] = useState(false);
  const [position, setPosition] = useState<ReturnType<typeof popupPosition> | null>(null);
  const [popupTheme, setPopupTheme] = useState<CSSProperties>({});
  const button = useRef<HTMLButtonElement>(null);
  const native = useRef<HTMLSelectElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const typeahead = useRef({ text: '', time: 0 });
  const opened = open && !disabled;

  function show(index = selectedIndex) {
    if (disabled) return;
    setActive(index >= 0 && !options[index]?.disabled ? index : moveOption(options, -1, 1));
    setPosition(null);
    setOpen(true);
  }

  function choose(index: number, restoreFocus = true) {
    const option = options[index];
    if (!disabled && option && !option.disabled && native.current) {
      if (option.value !== native.current.value) {
        native.current.value = option.value;
        native.current.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setInvalid(!native.current.validity.valid);
    }
    setOpen(false);
    typeahead.current.text = '';
    if (restoreFocus) button.current?.focus();
  }

  useEffect(() => { if (autoFocus) button.current?.focus(); }, [autoFocus]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  useEffect(() => {
    const form = native.current?.form;
    if (!form) return;
    const reset = () => queueMicrotask(() => {
      if (value === undefined) setLocalValue(initial.current);
      setInvalid(false);
      setOpen(false);
    });
    form.addEventListener('reset', reset);
    return () => form.removeEventListener('reset', reset);
  }, [value]);

  useLayoutEffect(() => {
    if (!opened) return;
    const place = (event?: Event) => {
      if (event?.type === 'scroll' && event.target instanceof Node && popup.current?.contains(event.target)) return;
      if (!button.current || !popup.current) return;
      const rect = button.current.getBoundingClientRect();
      const computed = getComputedStyle(button.current);
      const variables = ['--bg', '--bg-raised', '--bg-sunken', '--ink', '--ink-dim', '--ink-faint', '--line', '--line-strong', '--accent', '--accent-cool', '--radius', '--font-body'];
      setPopupTheme({ ...Object.fromEntries(variables.map(key => [key, computed.getPropertyValue(key)])), fontFamily: computed.fontFamily, colorScheme: computed.colorScheme });
      const view = window.visualViewport;
      const viewport = { left: view?.offsetLeft ?? 0, top: view?.offsetTop ?? 0,
        width: view?.width ?? window.innerWidth, height: view?.height ?? window.innerHeight };
      if (rect.bottom <= viewport.top || rect.top >= viewport.top + viewport.height) { setOpen(false); return; }
      const sizing = popupPosition(rect, viewport, 320);
      popup.current.style.width = `${sizing.width}px`;
      setPosition(popupPosition(rect, viewport, popup.current.scrollHeight));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [opened, options]);

  useEffect(() => {
    if (!opened) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !button.current?.contains(event.target) && !popup.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [opened]);

  useLayoutEffect(() => {
    if (!opened || !position || active < 0) return;
    const item = document.getElementById(`${listId}-${active}`);
    const list = popup.current;
    if (!item || !list) return;
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [active, opened, position, listId]);

  function keyDown(event: KeyboardEvent<HTMLButtonElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled) return;
    if (Date.now() - typeahead.current.time >= 700) typeahead.current.text = '';
    const first = () => moveOption(options, -1, 1);
    const last = () => moveOption(options, options.length, -1);
    if (event.key === 'Tab') {
      if (opened) choose(active, false);
      return;
    }
    if (event.key === 'Escape') {
      if (opened) { event.preventDefault(); event.stopPropagation(); setOpen(false); typeahead.current.text = ''; }
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const index = event.key === 'Home' ? first() : last();
      if (opened) setActive(index); else show(index);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (opened && event.altKey && event.key === 'ArrowUp') choose(active);
      else if (!opened) show(event.key === 'ArrowUp' ? first() : selectedIndex);
      else setActive(moveOption(options, active, event.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (event.key === 'Enter' || (event.key === ' ' && !typeahead.current.text)) {
      event.preventDefault();
      if (opened) choose(active); else show();
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const typed = now - typeahead.current.time < 700 ? typeahead.current.text + event.key : event.key;
      typeahead.current = { text: typed, time: now };
      const repeated = [...typed].every(char => char.toLocaleLowerCase() === typed[0].toLocaleLowerCase());
      const query = repeated ? event.key : typed;
      const index = matchOption(options, query, opened ? active : selectedIndex, query.length > 1);
      if (!opened) show(index < 0 ? selectedIndex : index);
      else if (index >= 0) setActive(index);
    }
  }

  const description = [nativeProps['aria-describedby'], invalid ? `${triggerId}-error` : null].filter(Boolean).join(' ') || undefined;
  return (
    <span className={`app-select${className ? ` ${className}` : ''}`} style={style}>
      <select
        {...nativeProps}
        ref={native}
        name={name}
        value={selectedValue}
        disabled={disabled}
        required={required}
        hidden
        tabIndex={-1}
        aria-hidden="true"
        onChange={event => { setLocalValue(event.target.value); setInvalid(false); onChange?.(event); }}
        onInvalid={event => { event.preventDefault(); setInvalid(true); show(); button.current?.focus(); onInvalid?.(event); }}
      >{children}</select>
      <button
        ref={button}
        id={triggerId}
        type="button"
        role="combobox"
        className="app-select__trigger"
        disabled={disabled}
        title={title}
        aria-label={nativeProps['aria-label']}
        aria-labelledby={nativeProps['aria-labelledby']}
        aria-describedby={description}
        aria-required={required}
        aria-invalid={invalid || nativeProps['aria-invalid']}
        aria-expanded={opened}
        aria-haspopup="listbox"
        aria-controls={opened ? listId : undefined}
        aria-activedescendant={opened && active >= 0 ? `${listId}-${active}` : undefined}
        onKeyDown={keyDown}
        onFocus={onFocus}
        onBlur={event => { if (opened) choose(active, false); onBlur?.(event); }}
        onClick={() => { if (opened) { setOpen(false); typeahead.current.text = ''; } else show(); }}
      >
        <span className="app-select__value">{selected?.label ?? options[0]?.label ?? 'Choose an option'}</span>
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="m5 7 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
      </button>
      {invalid && <span id={`${triggerId}-error`} className="app-select__error">Choose an option.</span>}
      {opened && createPortal(
        <div
          ref={popup}
          id={listId}
          role="listbox"
          aria-labelledby={triggerId}
          className="app-select__popup"
          style={{ ...popupTheme, ...(position ?? { visibility: 'hidden' }) }}
          onMouseDown={event => { event.preventDefault(); event.stopPropagation(); }}
          onClick={event => event.stopPropagation()}
        >
          {options.map((option, index) => <div key={`${option.value}-${index}`}>
            {option.group && options[index - 1]?.group !== option.group && <div className="app-select__group" aria-hidden="true">{option.group}</div>}
            <div
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              aria-disabled={option.disabled || undefined}
              aria-label={option.group ? `${option.group}: ${option.label}` : undefined}
              className={`app-select__option${active === index ? ' is-active' : ''}`}
              onPointerMove={event => { if (event.pointerType !== 'touch' && !option.disabled) setActive(index); }}
              onClick={() => { if (!option.disabled) choose(index); }}
            >
              <span>{option.label}</span><span className="app-select__check" aria-hidden="true">{index === selectedIndex ? '✓' : ''}</span>
            </div>
          </div>)}
          {!options.length && <div className="app-select__empty">No options available</div>}
        </div>, document.body,
      )}
    </span>
  );
}

export default Select;
